import React from 'react';
import CopyableSnippet from '../../components/utils/CopyableSnippet';
import LinkableTitle from '../../components/linkableTitle';

const Spacing = () => (
  <React.Fragment>
    <div className="page-header">
      <p>Core</p>
      <h1>Spacing</h1>
    </div>
    <div className="page-content">
      <div className="component-example__sub-example">
        <LinkableTitle name="basic-spacing-classes" title="Spacing function" />
        <blockquote>
          For layout purpose use the <CopyableSnippet code="grid(size)" /> function.
        </blockquote>
        <p>
          <code className="code--inline">(size)</code> values can be found below:
        </p>
        <table className="table margin-top-md margin-bottom-ml" data-test-id="table">
          <tbody>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                0
              </th>
              <td>0</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                1
              </th>
              <td>8px</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                2
              </th>
              <td>16px</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                3
              </th>
              <td>24px</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                4
              </th>
              <td>32px</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                5
              </th>
              <td>40px</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                6
              </th>
              <td>48px</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                7
              </th>
              <td>56px</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                8
              </th>
              <td>64px</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                9
              </th>
              <td>72px</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                10
              </th>
              <td>80px</td>
            </tr>
          </tbody>
        </table>
        <p>Example:</p>
        <div className="mb-1">
          <CopyableSnippet code={`.component {
  margin: grid(2) grid(3);
}`} lang="js" />
        </div>
        <p>this will return:</p>
        <CopyableSnippet code={`.component {
  margin: 16px 24px;
}`} lang="js" />
        <LinkableTitle
          name="spacing-utility-classes"
          title="Spacing utility classes"
        />
        <blockquote>
          For layout purpose feel free to use spacing classes
          <CopyableSnippet code="m-(size)" />
          <CopyableSnippet code="p-(size)" />
          <CopyableSnippet code="m(side)-(size)" />
          <CopyableSnippet code="p(side)-(size)" />
        </blockquote>
        <blockquote>
          Before using the spacing utility classes please include the apropiate mixin in your app:
        </blockquote>
        <div className="mb-1">
          <CopyableSnippet code="@include margin;" />
        </div>
        <div className="mb-5">
          <CopyableSnippet code="@include padding;" />
        </div>
        <p>The (side) values are:</p>
        <table className="table margin-top-md margin-bottom-ml" data-test-id="table">
          <tbody>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                t
              </th>
              <td>top</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                r
              </th>
              <td>right</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                b
              </th>
              <td>bottom</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                l
              </th>
              <td>left</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                x
              </th>
              <td>x-axis</td>
            </tr>
            <tr>
              <th scope="row" className="text-left padding-right-sm">
                y
              </th>
              <td>y-axis</td>
            </tr>
          </tbody>
        </table>
        <p>Examples:</p>
        <div className="mb-1">
          <CopyableSnippet code={`<div className="m-3 p-2">`} lang="js" />
        </div>
        <div className="mb-5">
          <CopyableSnippet code={`<div className="mt-1 pb-3">`} lang="js" />
        </div>
        <blockquote>
          Tip: Don't mix spacing classes with the component classes. We suggest using them outside the component.
        </blockquote>
        <p>Example:</p>
        <CopyableSnippet code={`<div className="m-3 p-2">
  <div className="component-class">
    component
  </div>
</div>`} lang="js" />
      </div>

      <div className="component-example__sub-example">
        <LinkableTitle name="combined-spacing-classes" title="Combined spacing classes" />
        <blockquote>To shorten html code combined classes can be used:</blockquote>
        <div className="mb-2">
          <CopyableSnippet code="mx-(size)" />
        </div>
        <div className="mb-2">
          <CopyableSnippet code="my-(size)" />
        </div>
        <div className="mb-2">
          <CopyableSnippet code="px-(size)" />
        </div>
        <div className="mb-2">
          <CopyableSnippet code="py-(size)" />
        </div>
        <p>Example:</p>
        <div className="mb-1">
        <CopyableSnippet code={`<div className="mx-3 py-2">
  ...
</div>`} lang="js" />
        </div>
        <p>this will return a <code>div</code> with these styles:</p>
        <CopyableSnippet code={`.div {
  margin-left: 24px;
  margin-right: 24px;
  padding-top: 16px;
  padding-bottom: 16px;
}`} lang="js" />
      </div>
    </div>
  </React.Fragment>
);

export default Spacing;
